import type { HomeLoaderData } from './homeLoader'
import { Popup } from 'antd-mobile'
import React, { useEffect, useEffectEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFetcher, useLoaderData } from 'react-router'
import { createOrder } from '@/api/createOrder'
import { getOrderStatus } from '@/api/getOrderStatus'
import BlockButton from '@/components/BlockButton/BlockButton'
import CloseBtn from '@/components/CloseBtn/CloseBtn'
import ScrollView from '@/components/ScrollView/ScrollView'
import Tabs from '@/components/Tabs/Tabs'
import { useGlobalContext } from '@/hooks/useGlobalContext'
import { usePageVisibility } from '@/hooks/usePageVisibility'
import i18n from '@/i18n/config'
import { openExternalBrowser } from '@/utils/native/func'
import DialogError from './components/DialogError'
import DialogNoVipSetting from './components/DialogNoVipSetting'
import DialogPayCompletedConfirm from './components/DialogPayCompletedConfirm'
import DialogSuccess from './components/DialogSuccess'
import DialogVipSetting from './components/DialogVipSetting'
import FeatureItem from './components/FeatureItem'
import PayButton from './components/PayButton/PayButton'
import PriceCard from './components/PriceCard'
import UserInfo from './components/UserInfo'

const features = [
  { type: 'marketing', label: i18n.t('home.wearVIP') }, // VIP标签佩戴
  { type: 'marketing', label: i18n.t('home.blockSplashAds') }, // 屏幕开屏广告
  { type: 'marketing', label: i18n.t('home.blockChatAds') }, // 屏幕会话页广告
  { type: 'marketing', label: i18n.t('home.blockDiscoveryAds') }, // 屏幕发现页广告
  { type: 'marketing', label: i18n.t('home.blockProfileAds') }, // 屏幕我的页面广告
  { type: 'marketing', label: i18n.t('home.blockMarketingAds') }, // 屏幕营销弹窗广告
  { type: 'marketing', label: i18n.t('home.blockMarketingAirdrop') }, // 屏幕横幅红包
  { type: 'utility', label: i18n.t('home.dedicatedNetwork') }, // 专属网络（提高链接稳定性）
  { type: 'utility', label: i18n.t('home.redPacketMonitor') }, // 红包监控
  { type: 'utility', label: i18n.t('home.increaseLoginLimit') }, // 增加登陆上限
  { type: 'utility', label: i18n.t('home.enableGroupJoinNotifications') }, // 开启入群通知
  { type: 'utility', label: i18n.t('home.blockChannelPage') }, // 屏蔽频道页面
  { type: 'utility', label: i18n.t('home.blockTrendingPage') }, // 屏蔽热榜页面
  { type: 'utility', label: i18n.t('home.blockWeb3KlinePage') }, // 屏蔽 Web3 K线功能页面
  { type: 'utility', label: i18n.t('home.blockBAYCPage') }, // 屏蔽BAYC页面
  { type: 'utility', label: i18n.t('home.blockTransactionBullet') }, // 屏蔽交易弹幕
  { type: 'utility', label: i18n.t('home.blockProfileAds') }, // 屏蔽我的页面广告
  { type: 'contact', label: i18n.t('home.vipExclusiveGroup') }, // VIP专属群组
  { type: 'contact', label: i18n.t('home.dedicatedSupport') }, // 专属客服
  { type: 'contact', label: i18n.t('home.requestFeature') }, // 想要什么功能，直接提
]
const OrderNoStorageKey = 'Btok_Vip_OrderNo'

const Home: React.FC = () => {
  console.log('Home 组件渲染')

  const fetcher = useFetcher()
  const refreshData = () => {
    void fetcher.load('.')
  }

  const { t, i18n } = useTranslation()
  const { username, platform, language, parentTGID } = useGlobalContext()
  const { products, vipInfo } = useLoaderData<HomeLoaderData>()
  const [selectedProductId, setSelectedProductId] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  // 初次渲染，设置默认选中产品，避免使用 useEffect 导致的重复渲染
  if (selectedProductId === 0 && products.length > 0 && products[0].id) {
    setSelectedProductId(products[0].id)
  }

  // 获取具体vip类型数据 使用 useMemo 避免重复计算
  const selectedProduct = useMemo(
    () => products.find(product => product.id === selectedProductId),
    [selectedProductId, products],
  )

  // 去支付
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const handleClick = async (type: 'ALIPAY' | 'WXPAY'): Promise<void> => {
    // return
    if (isLoading) return
    try {
      setIsLoading(true)
      const res = await createOrder({
        orderSource: platform,
        paymentChannel: type,
        productId: selectedProductId,
        rewardTgId: parentTGID ? parseInt(parentTGID) : null,
        productType: 'VIP',
        quantity: 1,
      })
      console.log('订单创建成功:', res)
      localStorage.setItem(OrderNoStorageKey, res.orderNo)
      const payUrl = res.payUrl.payUrl // 支付链接
      const qrCode = res.payUrl.qrCode // 二维码
      const urlScheme = res.payUrl.urlScheme // urlScheme
      const url = payUrl ?? qrCode ?? urlScheme ?? ''
      setTimeout(() => {
        setIsDrawerOpen(false)
        openExternalBrowser(url)
      })
    } catch (error) {
      console.error('订单创建失败:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const [activeTab, setActiveTab] = useState('marketing')
  const [vipSettingDialogOpen, setVipSettingDialogOpen] = useState(false)
  const [noVipSettingDialogOpen, setNoVipSettingDialogOpen] = useState(false)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [successDialogOpen, setSuccessDialogOpen] = useState(false)
  const [payCompletedDialogOpen, setPayCompletedDialogOpen] = useState(false)

  // 用户选择了我已支付，查询订单状态
  const handlePayConfirm = (selected: boolean) => {
    setPayCompletedDialogOpen(false)
    if (!selected) {
      localStorage.removeItem(OrderNoStorageKey)
      return
    }
    checkOrderStatus()
  }

  function checkOrderStatus() {
    const orderNo = localStorage.getItem(OrderNoStorageKey)
    if (!orderNo) return
    void getOrderStatus(orderNo)
      .then(res => {
        console.log('订单状态查询成功:', res)
        // "CANCEL" | "WAIT" | "FINISH"
        if (res === 'FINISH') {
          setSuccessDialogOpen(true)
          refreshData()
          localStorage.removeItem(OrderNoStorageKey)
        } else {
          // 订单状态不是已完成，提示用户稍后查询
          setTimeout(() => {
            setPayCompletedDialogOpen(true)
          }, 3000)
        }
      })
      .catch((err: unknown) => {
        console.error('订单状态查询失败:', err)
        setErrorDialogOpen(true)
        setTimeout(() => {
          setErrorDialogOpen(false)
          setPayCompletedDialogOpen(true)
        }, 3000)
      })
  }

  // 监听页面可见性变化，当用户从外部浏览器返回时检查订单状态
  // 页面初始加载时检查是否有未完成订单
  const isVisible = usePageVisibility()
  const isDialogOpen = useEffectEvent(() => payCompletedDialogOpen || successDialogOpen || errorDialogOpen)
  useEffect(() => {
    // console.log('页面可见性变化:============>', isVisible)
    if (isVisible) {
      const orderNo = localStorage.getItem(OrderNoStorageKey)
      // 只有当订单存在且当前没有打开支付完成对话框时，才显示询问
      if (orderNo && !isDialogOpen()) {
        setPayCompletedDialogOpen(true)
      }
    }
  }, [isVisible])

  const handleFeaturesClick = () => {
    if (vipInfo.effect) {
      // VIP 生效中
      setVipSettingDialogOpen(true)
    } else {
      setNoVipSettingDialogOpen(true)
    }
  }

  return (
    <div>
      {/* <p>语言：{language}</p>
      <p>TGID：{tgid}</p>
      <p>父TGID：{parentTGID}</p> */}
      {/* 用户信息卡片 */}
      <UserInfo username={username} data={vipInfo} />

      <ScrollView className="mx-3">
        {products.map(product => (
          <ScrollView.Item
            key={product.id}
            className={`p-[3px] box-border ${products.length >= 3 ? 'w-1/3' : 'w-4/13'}`}>
            <PriceCard isSelected={selectedProductId === product.id} data={product} onSelect={setSelectedProductId} />
          </ScrollView.Item>
        ))}
      </ScrollView>

      {/* 购买按钮 */}
      <div className="mx-3 my-6">
        <BlockButton
          disabled={language !== 'zh'}
          onClick={() => {
            setIsDrawerOpen(true)
          }}>
          {t('common.purchase')}
        </BlockButton>
      </div>

      {/* 抽屉效果 */}
      <Popup
        visible={isDrawerOpen}
        showCloseButton={false}
        maskClassName="__CustomMask__bg"
        onMaskClick={() => {
          setIsDrawerOpen(false)
        }}
        onClose={() => {
          setIsDrawerOpen(false)
        }}>
        <CloseBtn
          className="absolute top-3 right-3.5"
          onClick={() => {
            setIsDrawerOpen(false)
          }}
        />
        <div className="px-3 pt-2 pb-8">
          <div className="text-center mb-6 text-5">
            {selectedProduct?.productName[i18n.language as 'zh' | 'en' | 'ko' | 'vi'] ?? ''}
          </div>
          <div className="flex items-center justify-center pb-4">
            <span className="text-2xl">¥</span>
            <strong className="text-4xl">{selectedProduct?.ftPrice ?? selectedProduct?.discountPrice ?? ''}</strong>
          </div>
          <PayButton className="mt-3" type="ALIPAY" onClick={handleClick} isLoading={isLoading} />
          <PayButton className="mt-3" type="WXPAY" onClick={handleClick} isLoading={isLoading} />
        </div>
      </Popup>

      {/* <button
        onClick={() => {
          setPayCompletedDialogOpen(true)
        }}>
        ========= 测试 =========
      </button> */}
      <DialogNoVipSetting open={noVipSettingDialogOpen} onOpenChange={setNoVipSettingDialogOpen} />
      <DialogVipSetting open={vipSettingDialogOpen} onOpenChange={setVipSettingDialogOpen} />
      <DialogError open={errorDialogOpen} onOpenChange={setErrorDialogOpen} />
      <DialogSuccess open={successDialogOpen} onOpenChange={setSuccessDialogOpen} />
      <DialogPayCompletedConfirm open={payCompletedDialogOpen} onBtnClick={handlePayConfirm} />

      {/* 功能标签页 */}
      <Tabs
        tabs={[
          { id: 'marketing', label: t('home.marketing') },
          { id: 'utility', label: t('home.functions') },
          { id: 'contact', label: t('home.contact') },
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}>
        {features
          .filter(feature => feature.type === activeTab)
          .map((feature, index) => (
            <FeatureItem key={index} title={feature.label} onClick={handleFeaturesClick} />
          ))}
      </Tabs>
    </div>
  )
}

export default Home
